import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * TBM 일지 참여자 서명 QR (V577 · DOC-136 갈래 E).
 *
 * 편집 창 쪽(로그인) — 링크 열기 · 닫기, 서명한 사람 명단 · 지우기. 근로자 쪽(로그인 없음)은
 * `pages/public/TbmSignPage.tsx` 가 `/v1/safe-tbm-sign/{token}` 을 직접 부른다.
 */

/** 일지의 링크. 화면이 `/tbm-sign/{token}` 주소를 지어 QR 로 그린다. */
export type TbmSignLink = { token: string; openedAt: string; expiresAt: string; open: boolean; signed: number };

/** 서명 한 줄. signUrl 은 저장소가 꺼졌거나 그림이 없으면 null. */
export type TbmSignatureRow = {
  id: string;
  name: string;
  affiliation: string | null;
  jobType: string | null;
  signUrl: string | null;
  signedAt: string;
};

const base = (journalId: string) => `/v1/safe/journals/${journalId}/tbm-sign`;

export const tbmSignKeys = {
  link: (journalId: string) => ['safe-tbm-sign', 'link', journalId] as const,
  signatures: (journalId: string) => ['safe-tbm-sign', 'signatures', journalId] as const,
};

/** 근로자가 찍을 주소 — 지금 보고 있는 사이트의 주소 위에 짓는다. */
export function tbmSignUrl(token: string): string {
  return `${window.location.origin}/tbm-sign/${token}`;
}

export function useTbmSignLink(journalId: string | null) {
  return useQuery({
    queryKey: tbmSignKeys.link(journalId ?? ''),
    queryFn: () => apiFetch<TbmSignLink | null>(base(journalId!)),
    enabled: !!journalId,
    retry: false,
  });
}

/** 연다(open) · 닫는다(close). */
export function useTbmSignLinkWrite(journalId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: 'open' | 'close') =>
      apiFetch<TbmSignLink | null>(base(journalId!), { method: action === 'open' ? 'POST' : 'DELETE' }),
    onSuccess: (data) => queryClient.setQueryData(tbmSignKeys.link(journalId ?? ''), data),
  });
}

/** 서명 명단 — 링크가 열려 있는 동안 10초마다 다시 받는다(근로자가 내는 것이 보이게). */
export function useTbmSignatures(journalId: string | null, live: boolean) {
  return useQuery({
    queryKey: tbmSignKeys.signatures(journalId ?? ''),
    queryFn: () => apiFetch<TbmSignatureRow[]>(`${base(journalId!)}/signatures`),
    enabled: !!journalId,
    refetchInterval: live ? 10_000 : false,
    retry: false,
  });
}

export function useTbmSignatureDelete(journalId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signatureId: string) => apiFetch<void>(`${base(journalId!)}/signatures/${signatureId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['safe-tbm-sign'] }),
  });
}
